import React from "react";
import { OutputHeader } from "@/components/ai-assistant/OutputHeader";
import { CopyContentButton } from "@/components/ai-assistant/CopyContentButton";
import { Button } from "@/components/ui/button";
import {
  Star,
  Calendar,
  User as UserIcon,
  Share2,
  Sparkles,
} from "lucide-react";
import { ShareOutputDialog } from "@/components/ai-assistant/ShareOutputDialog";
import { EditableAIContentRenderer } from "@/components/ai-assistant/EditableAIContentRenderer";
import { useToast } from "@/components/ui/use-toast";

interface AIOutputViewProps {
  output: any;
  isSaved: boolean;
  onToggleSave: () => void;
  renderingSchema?: any;
  extraActionsRight?: React.ReactNode;
  onImprove?: () => void;
  onTitleSave?: (newTitle: string) => Promise<void> | void;
  isGenerating?: boolean;
  onContentSaved?: (updated: any) => void;
}

export function AIOutputView({
  output,
  isSaved,
  onToggleSave,
  renderingSchema,
  extraActionsRight,
  onImprove,
  onTitleSave,
  isGenerating = false,
  onContentSaved,
}: AIOutputViewProps) {
  const { toast } = useToast();
  const title: string = (output as any)?.output_title || "";

  // Check if this output is from a chat assistant
  const isChatAssistantOutput = React.useMemo(() => {
    const inputData = (output as any)?.input_data;
    return (
      inputData &&
      typeof inputData === "object" &&
      inputData.source === "chat_message"
    );
  }, [output]);

  const formFields = React.useMemo(() => {
    // Don't show form fields for chat assistant outputs
    if (isChatAssistantOutput) {
      return [] as Array<{ label: string }>;
    }

    const inputData = (output as any)?.input_data;
    if (!inputData || typeof inputData !== "object")
      return [] as Array<{ label: string }>;
    return Object.keys(inputData).map((key) => ({ label: key }));
  }, [output, isChatAssistantOutput]);

  return (
    <div className="p-6">
      <div className="max-w-[750px] mx-auto">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <OutputHeader
              title={title}
              formFields={formFields}
              formData={
                isChatAssistantOutput ? {} : (output as any)?.input_data || {}
              }
              canEditTitle={true}
              onTitleSave={onTitleSave}
              belowTitleSlot={
                (output as any)?.is_edit_mode ? (
                  <div className="relative">
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded">
                      edit mode
                    </span>
                    <button
                      className="absolute -top-1 right-0 h-7 w-7 inline-flex items-center justify-center rounded bg-white border text-gray-600 hover:bg-gray-50"
                      title="Full screen"
                      onClick={() => {
                        const ev = new CustomEvent("editor-enter-fullscreen", {
                          detail: { persistKey: (output as any)?.id },
                        });
                        window.dispatchEvent(ev);
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                      >
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <polyline points="9 21 3 21 3 15"></polyline>
                        <line x1="21" y1="3" x2="14" y2="10"></line>
                        <line x1="3" y1="21" x2="10" y2="14"></line>
                      </svg>
                    </button>
                  </div>
                ) : null
              }
            />
          </div>
        </div>
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-500 flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date((output as any)?.created_at).toLocaleDateString(
                undefined,
                { year: "numeric", month: "short", day: "numeric" },
              )}
            </span>
            {!!(output as any)?.user_id && (
              <span className="inline-flex items-center gap-1">
                <UserIcon className="h-3 w-3" />
                {(output as any)?.user_name || "User"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onImprove && (
              <Button
                size="sm"
                onClick={onImprove}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 text-[rgb(158,172,211)] border border-[rgb(158,172,211)] hover:border-[rgb(140,156,195)] hover:text-[rgb(140,156,195)]"
              >
                <Sparkles className="h-4 w-4" />
                Improve
              </Button>
            )}
            {!(output as any)?.is_edit_mode && (
              <>
                {(output as any)?.id && (
                  <ShareOutputDialog outputId={(output as any).id}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <Share2 className="h-4 w-4" /> Share
                    </Button>
                  </ShareOutputDialog>
                )}
                <CopyContentButton
                  content={
                    (output as any)?.structured_output ||
                    (output as any)?.output_data ||
                    "No content available"
                  }
                  title="Saved AI Output"
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                />
              </>
            )}
            {extraActionsRight}
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleSave}
              className="h-8 w-8"
              title={isSaved ? "Unstar" : "Star"}
            >
              <Star
                className={`h-4 w-4 ${isSaved ? "text-yellow-500" : ""}`}
                fill={isSaved ? "currentColor" : "none"}
              />
            </Button>
            {/* Save to folder - provided by parent via extraActionsRight (FolderAssignDropdown) */}
          </div>
        </div>
        {/* Generating indicator (matches Generate tab behavior) */}
        {(console.log(
          "🔍 AIOutputView isGenerating:",
          isGenerating,
          "output.id:",
          (output as any)?.id,
        ),
        isGenerating) ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-yellow-600">
              <div className="h-2 w-2 bg-yellow-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Generating content...</span>
            </div>
            {((output as any)?.structured_output ||
              (output as any)?.output_data) && (
              <div className="border-2 border-dashed border-yellow-200 rounded-lg p-4">
                <EditableAIContentRenderer
                  key={(output as any)?.id}
                  content={
                    (output as any)?.structured_output ||
                    (output as any)?.output_data
                  }
                  onSave={async (newContent) => {
                    const isEdit = Boolean((output as any)?.is_edit_mode);
                    if (!isEdit) {
                      console.log(
                        "⚠️ NO-OP onSave called (generating state, not in edit mode)",
                      );
                      return;
                    }
                    try {
                      console.log(
                        "🚀 MAIN onSave (generating) - going to database",
                      );
                      if (!(output as any)?.id) return;
                      const svc = (
                        await import("@/services/aiAssistantService")
                      ).aiAssistantService;
                      const { data, error } = await svc.updateAIOutputContent(
                        (output as any).id,
                        newContent,
                      );
                      if (error) {
                        console.error("❌ Database save failed:", error);
                        toast({
                          variant: "destructive",
                          title: "Save failed",
                          description:
                            error.message || "Could not save changes.",
                        });
                        return;
                      }
                      console.log("✅ Database save successful:", data);
                      if (onContentSaved && data) onContentSaved(data);
                    } catch (e: any) {
                      console.error("❌ Unexpected error during save:", e);
                      toast({
                        variant: "destructive",
                        title: "Save failed",
                        description: e?.message || "Could not save changes.",
                      });
                    }
                  }}
                  canEdit={Boolean((output as any)?.is_edit_mode)}
                  alwaysEdit={Boolean((output as any)?.is_edit_mode)}
                  persistKey={(output as any)?.id}
                  onEnterEdit={async () => {
                    try {
                      if (!(output as any)?.id) return;
                      const svc = (
                        await import("@/services/aiAssistantService")
                      ).aiAssistantService;
                      await svc.setOutputEditMode((output as any).id, true);
                    } catch (_) {}
                  }}
                  schema={renderingSchema}
                />
                <div className="flex items-center gap-2 text-sm text-yellow-600 pt-3 border-t border-yellow-200 mt-3">
                  <div className="h-2 w-2 bg-yellow-500 rounded-full animate-pulse"></div>
                  <span>Content is being generated in real-time...</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <EditableAIContentRenderer
            key={(output as any)?.id}
            content={
              typeof (output as any)?.structured_output === "string"
                ? (output as any)?.structured_output
                : undefined
            }
            initialJson={
              typeof (output as any)?.structured_output === "object" &&
              (output as any)?.structured_output !== null
                ? ((output as any)?.structured_output as any)
                : undefined
            }
            alwaysEdit={Boolean((output as any)?.is_edit_mode)}
            persistKey={(output as any)?.id}
            onEnterEdit={async () => {
              try {
                if (!(output as any)?.id) return;
                const svc = (await import("@/services/aiAssistantService"))
                  .aiAssistantService;
                await svc.setOutputEditMode((output as any).id, true);
              } catch (_) {}
            }}
            onSave={async (newContent) => {
              console.log("🚀 MAIN onSave called - this should go to database");
              try {
                console.log(
                  "🔄 Starting database save for output:",
                  (output as any)?.id,
                );
                console.log("📝 Content to save:", newContent);
                if (!(output as any)?.id) {
                  console.log("❌ No output ID found, aborting save");
                  return;
                }
                const svc = (await import("@/services/aiAssistantService"))
                  .aiAssistantService;
                const { data, error } = await svc.updateAIOutputContent(
                  (output as any).id,
                  newContent,
                );
                if (error) {
                  console.error("❌ Database save failed:", error);
                  toast({
                    variant: "destructive",
                    title: "Save failed",
                    description: error.message || "Could not save changes.",
                  });
                  return;
                }
                console.log("✅ Database save successful:", data);
                if (onContentSaved && data) onContentSaved(data);
                toast({ title: "Saved" });
              } catch (e: any) {
                console.error("❌ Unexpected error during save:", e);
                toast({
                  variant: "destructive",
                  title: "Save failed",
                  description: e?.message || "Could not save changes.",
                });
              }
            }}
            canEdit={true}
            schema={renderingSchema}
          />
        )}
      </div>
    </div>
  );
}
